import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import type { NavRole } from '../components/navModel';
import {
  accessFromServer, canOpenPath, canScreen as canScreenIn, fallbackAccess, hasScope as hasScopeIn,
  isPortalIdentity, noAccess, openedByOverride as openedByOverrideIn, parseMyPermissions, screenKey, seesAllEmployees as seesAllEmployeesIn, type MyCapabilities, type MyPermissions, type ScreenAccess,
} from '../components/permissions/screenAccess';

/**
 * The signed-in person's screens and scopes (`GET /api/permissions/me`).
 *
 * - Loaded once per user (react-query, keyed by the user id, so switching
 *   users loads again) and kept for the session; the permissions screen
 *   invalidates `PERMISSIONS_ME_KEY` after a change.
 * - Older server (404) or no answer: the role's defaults, exactly the
 *   nav model's role rules, so the UI keeps working against an old API.
 * - The last answer is kept per user on this device and shown while the
 *   fresh one loads, so the nav does not flash empty on reload. Until there
 *   is an answer nothing is open (`ready` is false): a blocked screen never
 *   flashes into view.
 */
export const PERMISSIONS_ME_KEY = ['permissions', 'me'] as const;

type MeResult = { source: 'server'; me: MyPermissions } | { source: 'fallback' };

const CACHE_PREFIX = 'ote_permissions_me:';

function readCache(userId: string): MeResult | undefined {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + userId);
    const me = raw ? parseMyPermissions(JSON.parse(raw)) : null;
    return me ? { source: 'server', me } : undefined;
  } catch {
    return undefined;
  }
}

function writeCache(userId: string, me: MyPermissions | null) {
  try {
    if (me) localStorage.setItem(CACHE_PREFIX + userId, JSON.stringify(me));
    else localStorage.removeItem(CACHE_PREFIX + userId);
  } catch {
    // storage unavailable: the cache is a convenience only
  }
}

const statusOf = (err: unknown): number | undefined => (err as { response?: { status?: number } } | null)?.response?.status;

export interface Permissions {
  /** An answer (or the role fallback) is in; before that nothing is open. */
  ready: boolean;
  /** `fallback`: the server has no permissions endpoint (or did not answer). */
  source: 'server' | 'fallback' | 'loading';
  isAdmin: boolean;
  /** SEC-2a (D-91 ב5): a customer identity — its home is /portal. */
  portalOnly: boolean;
  /** SEC-2a (D-91 ב10): what the server allows (buttons follow it). */
  capabilities: MyCapabilities;
  /** D-125: the server's `isOteEmployee` (employee profile); undefined until it answers / on an older server. */
  isOteEmployee: boolean | undefined;
  /** P-33c (D-185): internal-work-only employee — no customers, tickets or approvals anywhere. */
  internalOnly: boolean;
  /** P-33c: the engagement "עבודה פנימית" her tasks are created on. */
  internalWorkEngagementId: string | null;
  internalWorkOrganizationId: string | null;
  access: ScreenAccess;
  /** `canScreen('screen:billing')` or the nav id alone, `canScreen('billing')`. */
  canScreen: (key: string) => boolean;
  /** D-335 — does his own "view" / "edit" override open this screen (`'logDetail'` or `'screen:logDetail'`)? */
  openedByOverride: (key: string) => boolean;
  /** FIX-PERMS-SCOPE — does he see this screen (`'attendance'` or `'screen:attendance'`) in the scope "all employees"? Read only, no money. */
  seesAllEmployees: (key: string) => boolean;
  hasScope: (key: string) => boolean;
  /** May an in-app link to this path be shown as a link? */
  canPath: (path: string) => boolean;
}

export function usePermissions(): Permissions {
  const userId = useAuthStore((s) => s.user?.id);
  const role = useAuthStore((s) => s.user?.role) as NavRole;
  const portalHint = useAuthStore((s) => isPortalIdentity(s.user));

  const { data, isError } = useQuery<MeResult>({
    queryKey: [...PERMISSIONS_ME_KEY, userId],
    enabled: !!userId,
    queryFn: async () => {
      try {
        const me = parseMyPermissions(await api.getMyPermissions());
        if (userId) writeCache(userId, me);
        return me ? { source: 'server', me } : { source: 'fallback' };
      } catch (err) {
        if (statusOf(err) === 404) {
          if (userId) writeCache(userId, null);
          return { source: 'fallback' };
        }
        throw err;
      }
    },
    placeholderData: () => (userId ? readCache(userId) : undefined),
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    retry: (count, err) => count < 1 && statusOf(err) !== 401 && statusOf(err) !== 403,
  });

  const access = useMemo<ScreenAccess>(() => {
    if (data?.source === 'server') return accessFromServer(data.me, role, portalHint);
    if (data?.source === 'fallback' || isError) return fallbackAccess(role, portalHint);
    return noAccess(role, portalHint);
  }, [data, isError, role, portalHint]);

  const ready = !!data || isError;
  const canScreen = useCallback((key: string) => canScreenIn(access, key.includes(':') ? key : screenKey(key)), [access]);
  const openedByOverride = useCallback((key: string) => openedByOverrideIn(access, key.includes(':') ? key : screenKey(key)), [access]);
  const seesAllEmployees = useCallback((key: string) => seesAllEmployeesIn(access, key.includes(':') ? key : screenKey(key)), [access]);
  const hasScope = useCallback((key: string) => hasScopeIn(access, key), [access]);
  const canPath = useCallback((path: string) => canOpenPath(access, path), [access]);

  return {
    ready,
    source: data?.source ?? (isError ? 'fallback' : 'loading'),
    isAdmin: access.isAdmin,
    portalOnly: access.portalOnly,
    capabilities: access.capabilities,
    isOteEmployee: data?.source === 'server' ? data.me.isOteEmployee : undefined,
    internalOnly: access.internalOnly,
    internalWorkEngagementId: access.internalWorkEngagementId,
    internalWorkOrganizationId: access.internalWorkOrganizationId,
    access,
    canScreen,
    openedByOverride,
    seesAllEmployees,
    hasScope,
    canPath,
  };
}
